import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { AIAnswerError } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	onAIAnswerResultFirstByteShown,
} from '../../ai-answer-dialog/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { type QueryIntent } from '../../constants';
import { AnimatedSingleLineShowMore } from '../common/ui/animated-single-line-show-more';
import { SmartAnswerCardBodyGenerating } from '../common/ui/smart-answer-card-body-generating';

import { SmartAnswerCardBodyComplete } from './smart-answer-card-body-complete';
import { SmartAnswerCardBodyStreaming } from './smart-answer-card-body-streaming';

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
	},
	interactive: {
		cursor: 'pointer',
	},
	loadingPaddingOverrides: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
});

type SmartAnswerCardComponentProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	queryIntent?: QueryIntent;
};

function SmartAnswerCardComponent({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardComponentProps) {
	const [collapsed, setCollapsed] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const firstByteEventFiredRef = useRef(false);

	const { commonAttributes, hasError, isAnswered, loading, partialResponse } = useAIAnswerContext();
	const { cloudId, queryHash, searchSessionId } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	useEffect(() => {
		if (streamingText.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [streamingText, commonAttributes, fireAnalyticsEvent]);

	useEffect(() => {
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached]);

	// use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const selection = window.getSelection()?.toString() ?? '';
		// Highlighting text should not expand the card
		const hasHighlightedText = selection.length > 0;
		// Clicking in a feedback modal or flag should not expand the card
		const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

		if (collapsed && !hasHighlightedText && isClickingOnContainer) {
			setCollapsed(false);
			fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		}
	};

	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	let content: React.JSX.Element | null = null;

	if (!isAnswered && (loading || loading === undefined)) {
		if (!partialResponse) {
			content = <SmartAnswerCardBodyGenerating />;
		} else {
			content = (
				<SmartAnswerCardBodyStreaming
					cloudId={cloudId}
					collapsed={collapsed}
					onExpandCollapseButtonClick={handleExpandCollapseButtonClick}
					onVisibleTextComplete={handleVisibleTextComplete}
					streamedTextToDisplay={streamingTextToDisplay}
				/>
			);
		}

		content = (
			<>
				<UFOLoadHold name="search-ai-dialog-all-text-loading" />
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
						<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
						<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
					</>
				) : (
					<UFOCustomData
						data={{
							queryHash,
							searchSessionId,
							queryIntent,
						}}
					/>
				)}
				{content}
			</>
		);
	} else {
		content = (
			<>
				{/* This Box click handler is just a convenience for mouse users; there is a keyboard-accessible button that supports the same functionality */}
				{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
				<Box
					xcss={collapsed ? styles.interactive : undefined}
					onClick={handleContainerClick}
					ref={containerRef}
				>
					<SmartAnswerCardBodyComplete
						collapsed={collapsed}
						onExpandCollapseButtonClick={handleExpandCollapseButtonClick}
						queryIntent={queryIntent}
					/>
				</Box>
			</>
		);
	}

	content = (
		<UFOSegment name="search-ai-dialog-all-text">
			<UFOSegment name="search-ai-dialog-visible-text">
				{isVisibleTextComplete ? null : (
					<UFOLoadHold name="search-ai-dialog-visible-text-loading" />
				)}
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
						<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
						<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
					</>
				) : (
					<UFOCustomData
						data={{
							queryHash,
							searchSessionId,
							queryIntent,
						}}
					/>
				)}
				{content}
			</UFOSegment>
		</UFOSegment>
	);

	if (SAINFeatureControl.isLuminaEnabled()) {
		if (!isAnswered && (loading || loading === undefined)) {
			return (
				<Box xcss={cx(styles.fullWidth, styles.loadingPaddingOverrides)}>
					{content}
					<AnimatedSingleLineShowMore isStreaming showMoreButtonVisible={false} />
				</Box>
			);
		} else {
			return <Box xcss={styles.fullWidth}>{content}</Box>;
		}
	}

	return (
		<AnimatedWrapper isAnswerComplete={isAnswered && !loading}>
			<Box xcss={styles.fullWidth}>{content}</Box>
		</AnimatedWrapper>
	);
}

export function SmartAnswerCard({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardComponentProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				queryIntent={queryIntent}
			/>
		</UFOSegment>
	);
}
